import * as THREE from 'three';
import {
  CAMERA_FRUSTUM_SIZE,
  CAMERA_MIN_WIDTH,
  CAMERA_NEAR,
  CAMERA_FAR,
  CAMERA_ANGLE,
} from '@/utils/constants';
import { EventBus, GameEvents } from '@/core/EventBus';

/**
 * Orthographic camera for tactical hex grid view
 */
export class Camera {
  public camera: THREE.OrthographicCamera;
  private eventBus: EventBus;

  constructor(eventBus: EventBus) {
    this.eventBus = eventBus;

    const { frustumSize, aspect } = this.calculateFrustumSize();
    const frustumHalf = frustumSize / 2;

    this.camera = new THREE.OrthographicCamera(
      -frustumHalf * aspect,
      frustumHalf * aspect,
      frustumHalf,
      -frustumHalf,
      CAMERA_NEAR,
      CAMERA_FAR
    );

    this.setupPosition();
    this.setupResizeHandler();
  }

  private setupPosition(): void {
    // Position camera above and slightly angled
    const distance = 15;
    this.camera.position.set(
      0,
      distance * Math.cos(CAMERA_ANGLE),
      distance * Math.sin(CAMERA_ANGLE)
    );
    this.camera.lookAt(0, 0, 0);
  }

  private setupResizeHandler(): void {
    this.eventBus.on(GameEvents.WINDOW_RESIZED, () => {
      this.updateAspect();
    });
  }

  /**
   * Calculate the frustum size based on window dimensions.
   * On narrow screens, increases frustum to maintain minimum visible width.
   */
  private calculateFrustumSize(): { frustumSize: number; aspect: number } {
    const aspect = window.innerWidth / window.innerHeight;

    // Calculate what horizontal view we'd get with default frustum
    const defaultWidth = CAMERA_FRUSTUM_SIZE * aspect;

    // If the width would be less than minimum, scale up the frustum
    if (defaultWidth < CAMERA_MIN_WIDTH) {
      const frustumSize = CAMERA_MIN_WIDTH / aspect;
      return { frustumSize, aspect };
    }

    return { frustumSize: CAMERA_FRUSTUM_SIZE, aspect };
  }

  /**
   * Update camera aspect ratio on resize
   */
  updateAspect(): void {
    const { frustumSize, aspect } = this.calculateFrustumSize();
    const frustumHalf = frustumSize / 2;

    this.camera.left = -frustumHalf * aspect;
    this.camera.right = frustumHalf * aspect;
    this.camera.top = frustumHalf;
    this.camera.bottom = -frustumHalf;
    this.camera.updateProjectionMatrix();
  }

  /**
   * Set the camera target position
   */
  setTarget(x: number, y: number, z: number): void {
    this.camera.lookAt(x, y, z);
  }

  /**
   * Get the Three.js camera instance
   */
  get instance(): THREE.OrthographicCamera {
    return this.camera;
  }
}
